import { MP4Demuxer } from './MP4Demuxer';
import {
  DEFAULT_RENDER_OPERATION_TIMEOUT_MS,
  getRenderAbortError,
  throwIfRenderAborted,
  waitForRenderOperation
} from './RenderTask';

const MAX_DECODE_QUEUE_SIZE = 8;
const MAX_DECODED_FRAME_QUEUE_SIZE = 8;

export class VideoDecoderPipeline {
  private decoder: VideoDecoder | null = null;
  private demuxer: MP4Demuxer | null = null;
  private frameQueue: VideoFrame[] = [];
  private nextFrameWaiters: Array<{
    resolve: (frame: VideoFrame | null) => void;
    reject: (error: Error) => void;
  }> = [];
  private capacityWaiters = new Set<() => void>();
  private isEnded = false;
  private isClosed = false;
  private failure: Error | null = null;
  private currentFrame: VideoFrame | null = null;
  private lastGoodFrame: VideoFrame | null = null;
  private timestampOriginMicros: number | null = null;
  private initializationReject: ((error: Error) => void) | null = null;
  private readonly handleDecoderDequeue = () => {
    this.signalCapacityChange();
  };
  private readonly handleSignalAbort = () => {
    this.fail(getRenderAbortError(this.signal));
  };

  constructor(
    private url: string,
    private readonly signal?: AbortSignal
  ) {
    this.signal?.addEventListener('abort', this.handleSignalAbort, { once: true });
  }

  async initialize(): Promise<void> {
    throwIfRenderAborted(this.signal);
    return new Promise((resolve, reject) => {
      let initializationSettled = false;
      let initializationTimeoutId: number | null = null;
      const cleanupInitialization = () => {
        if (initializationTimeoutId !== null) {
          window.clearTimeout(initializationTimeoutId);
          initializationTimeoutId = null;
        }
        this.initializationReject = null;
      };
      const resolveInitialization = () => {
        if (!initializationSettled) {
          initializationSettled = true;
          cleanupInitialization();
          resolve();
        }
      };
      const rejectInitialization = (error: Error) => {
        if (!initializationSettled) {
          initializationSettled = true;
          cleanupInitialization();
          reject(error);
        }
      };
      this.initializationReject = rejectInitialization;
      const handleFailure = (error: unknown) => {
        const normalized = error instanceof Error ? error : new Error(String(error));
        this.fail(normalized);
        rejectInitialization(normalized);
      };
      initializationTimeoutId = window.setTimeout(
        () => handleFailure(new Error('Video decoder initialization stopped responding.')),
        DEFAULT_RENDER_OPERATION_TIMEOUT_MS
      );

      this.decoder = new VideoDecoder({
        output: (frame) => {
          if (this.isClosed || this.failure) {
            frame.close();
            return;
          }
          const waiter = this.nextFrameWaiters.shift();
          if (waiter) {
            waiter.resolve(frame);
          } else {
            this.frameQueue.push(frame);
          }
          this.signalCapacityChange();
        },
        error: (error) => {
          console.error('VideoDecoder error', error);
          handleFailure(error);
        }
      });
      this.decoder.addEventListener('dequeue', this.handleDecoderDequeue);

      this.demuxer = new MP4Demuxer(this.url, {
        onConfig: (config) => {
          if (this.decoder!.state === 'unconfigured') {
            this.decoder!.configure(config);
          }
        },
        onChunk: async (chunk) => {
          await this.waitForDecodeCapacity();
          if (!this.decoder || this.decoder.state !== 'configured') {
            throw new Error('VideoDecoder is not configured.');
          }
          this.decoder.decode(chunk);
        },
        onReady: () => {
          resolveInitialization();
        },
        onError: (message) => {
          handleFailure(new Error(message));
        }
      });

      this.demuxer.initialize()
        .then(async () => {
          if (this.isClosed || this.failure || !this.decoder || this.decoder.state !== 'configured') {
            return;
          }
          await waitForRenderOperation(this.decoder.flush(), {
            signal: this.signal,
            label: 'Video decoder flush'
          });
          this.finishDecoding();
        })
        .catch(handleFailure);
    });
  }

  async seekToTime(seconds: number): Promise<VideoFrame | null> {
    const targetMicros = Math.round(seconds * 1_000_000);
    
    while (true) {
      if (!this.currentFrame && this.frameQueue.length === 0 && this.isEnded) {
        return this.lastGoodFrame ? this.lastGoodFrame.clone() : null;
      }
      
      if (!this.currentFrame) {
         if (this.frameQueue.length > 0) {
           this.currentFrame = this.frameQueue.shift()!;
           this.signalCapacityChange();
         } else {
           this.currentFrame = await this.waitForNextFrame();
           if (!this.currentFrame) return null;
         }
      }

      // If decoding jumped past the requested timestamp, keep the atlas on the
      // last frame that actually covered the target. Using the future frame here
      // desynchronizes atlas masks from tracking on fast motion.
      const frameStart = this.getFrameStartMicros(this.currentFrame);
      if (frameStart > targetMicros + 20_000) {
         return this.lastGoodFrame ? this.lastGoodFrame.clone() : this.currentFrame.clone();
      }
      
      const duration = this.currentFrame.duration || 33333; // guess 30fps if absent
      const frameEnd = frameStart + duration;
      
      // If the target time falls strictly into a FUTURE frame, drop the current one
      if (targetMicros >= frameEnd - 5_000) {
         this.currentFrame.close();
         this.currentFrame = null;
         continue;
      }

      // Frame covers the target time!
      this.storeLastGoodFrame(this.currentFrame);
      return this.currentFrame.clone();
    }
  }

  private storeLastGoodFrame(frame: VideoFrame): void {
    if (this.lastGoodFrame) {
      this.lastGoodFrame.close();
    }
    this.lastGoodFrame = frame.clone();
  }

  private getFrameStartMicros(frame: VideoFrame): number {
    if (this.timestampOriginMicros === null) {
      this.timestampOriginMicros = frame.timestamp;
    }
    return frame.timestamp - this.timestampOriginMicros;
  }

  private async waitForNextFrame(): Promise<VideoFrame | null> {
    if (this.failure) throw this.failure;
    if (this.isEnded && this.frameQueue.length === 0) return null;
    return new Promise((resolve, reject) => {
      let timeoutId: number | null = null;
      const cleanup = () => {
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }
      };
      const waiter = {
        resolve: (frame: VideoFrame | null) => {
          cleanup();
          resolve(frame);
        },
        reject: (error: Error) => {
          cleanup();
          reject(error);
        }
      };
      this.nextFrameWaiters.push(waiter);
      timeoutId = window.setTimeout(
        () => this.fail(new Error('Video decoder stopped producing frames.')),
        DEFAULT_RENDER_OPERATION_TIMEOUT_MS
      );
    });
  }

  private async waitForDecodeCapacity(): Promise<void> {
    while (
      !this.isClosed &&
      !this.failure &&
      this.decoder &&
      (this.decoder.decodeQueueSize >= MAX_DECODE_QUEUE_SIZE ||
        this.frameQueue.length >= MAX_DECODED_FRAME_QUEUE_SIZE)
    ) {
      await new Promise<void>((resolve) => {
        let timeoutId: number | null = null;
        const waiter = () => {
          if (timeoutId !== null) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
          }
          resolve();
        };
        this.capacityWaiters.add(waiter);
        timeoutId = window.setTimeout(
          () => this.fail(new Error('Video decoder queue stopped responding.')),
          DEFAULT_RENDER_OPERATION_TIMEOUT_MS
        );
      });
    }

    if (this.failure) {
      throw this.failure;
    }
    if (this.isClosed) {
      throw new Error('Video decoder pipeline is closed.');
    }
  }

  private signalCapacityChange(): void {
    const waiters = Array.from(this.capacityWaiters);
    this.capacityWaiters.clear();
    waiters.forEach((resolve) => resolve());
  }

  private finishDecoding(): void {
    this.isEnded = true;
    while (this.nextFrameWaiters.length > 0) {
      this.nextFrameWaiters.shift()!.resolve(null);
    }
    this.signalCapacityChange();
  }

  private fail(error: Error): void {
    if (this.failure || this.isClosed) {
      return;
    }
    this.failure = error;
    this.isEnded = true;
    this.initializationReject?.(error);
    this.demuxer?.abort();
    while (this.nextFrameWaiters.length > 0) {
      this.nextFrameWaiters.shift()!.reject(error);
    }
    this.signalCapacityChange();
  }

  close(): void {
    if (this.isClosed) {
      return;
    }
    this.isClosed = true;
    this.isEnded = true;
    this.initializationReject?.(new Error('Video decoder pipeline is closed.'));
    this.demuxer?.abort();
    while (this.nextFrameWaiters.length > 0) {
      this.nextFrameWaiters.shift()!.resolve(null);
    }
    this.signalCapacityChange();
    if (this.currentFrame) {
      this.currentFrame.close();
      this.currentFrame = null;
    }
    if (this.lastGoodFrame) {
      this.lastGoodFrame.close();
      this.lastGoodFrame = null;
    }
    this.frameQueue.forEach((f) => f.close());
    this.frameQueue = [];
    if (this.decoder && this.decoder.state !== 'closed') {
      this.decoder.removeEventListener('dequeue', this.handleDecoderDequeue);
      this.decoder.close();
    }
    this.signal?.removeEventListener('abort', this.handleSignalAbort);
  }
}
